import React, { useEffect, useState } from 'react';
import { Button } from '@planne-software/uni/Button';
import { Divider } from '@planne-software/uni/mui/material';
import { Modal, ModalActions, ModalHeader } from '@planne-software/uni/Modal';
import { IconArrowRight, IconX } from '@planne-software/uni/icons';

import { SaleFormInput } from '@src/core/buyerApiClient';
import { useTranslate } from '@src/core/hooks';
import { formInputGroupKey, type FormInputValues } from '@src/domains/orders';
import type { QueuedTariff } from '@src/features/orders/form-inputs/core/buildTariffFormInputQueue';

import { useTariffFormInputsForm } from '@src/features/orders/form-inputs/hooks/useTariffFormInputsForm';
import {
  MODAL_FIELD_SPACING,
  ModalFields,
  ModalStackedContent,
  ModalTariffBlock,
  ModalTariffLine,
  ModalTariffName,
  responsiveModalSx,
} from '@src/features/orders/form-inputs/styles';

import { AdditionalInfoChip } from './AdditionalInfoChip';
import { TariffFormFields } from './TariffFormFields';

/**
 * `wizard` walks one tariff at a time: that is the filling, and also the review after a
 * 422. `edit` stacks the units of one row at once, because whoever already filled them
 * and clicks edit wants to see what they wrote, not to step through it again.
 */
export const ADDITIONAL_INFO_MODAL_MODE = {
  wizard: 'wizard',
  edit: 'edit',
} as const;

export type AdditionalInfoModalMode = (typeof ADDITIONAL_INFO_MODAL_MODE)[keyof typeof ADDITIONAL_INFO_MODAL_MODE];

type AdditionalInfoModalProps = {
  open: boolean;
  mode: AdditionalInfoModalMode;
  /** The tariffs this modal walks, in order, crossing cart items. */
  queue: QueuedTariff[];
  productNameFor: (saleItemId: string) => string;
  storeErrors: Record<string, string[]>;
  values: FormInputValues;
  onSaveFormInput: (patch: FormInputValues) => void;
  /** Reports which cart item the wizard is on, so the accordion can follow. */
  onStepChange?: (saleItemId: string) => void;
  onClose: () => void;
};

const tariffName = (inputs: SaleFormInput[]): string => inputs[0]?.saleTariff?.name ?? '';

type WizardStepProps = {
  inputs: SaleFormInput[];
  productName: string;
  unitIndex: number;
  unitCount: number;
  isLast: boolean;
  storeErrors: Record<string, string[]>;
  values: FormInputValues;
  onSaveFormInput: (patch: FormInputValues) => void;
  onAdvance: () => void;
  onClose: () => void;
};

/**
 * One wizard step. It is its own component, mounted with a `key` per tariff, so that
 * react-hook-form reloads the new tariff's defaultValues instead of keeping the
 * previous ones: hooks cannot be reinitialised any other way.
 */
const WizardStep = (props: WizardStepProps) => {
  const {
    inputs,
    isLast,
    productName,
    unitIndex,
    unitCount,
    storeErrors,
    values,
    onSaveFormInput,
    onClose,
    onAdvance,
  } = props;
  const { i18n } = useTranslate();

  const { control, errors, canSubmit, submit } = useTariffFormInputsForm({ inputs, values, errors: storeErrors });

  const handleAccept = submit((collected) => {
    onSaveFormInput(collected);

    if (isLast) {
      onClose();
      return;
    }

    onAdvance();
  });

  return (
    <>
      <ModalHeader title={productName} onClose={onClose} divider paddingBottom>
        {/*
          The list collapses the units into one row ("2x adult"); the badge says which
          one is being filled. With a single unit it does not show.
        */}
        <ModalTariffLine>
          <ModalTariffName>{tariffName(inputs)}</ModalTariffName>
          {unitCount > 1 ? (
            <AdditionalInfoChip
              tone={unitIndex === unitCount ? 'success' : 'primary'}
              label={`${unitIndex}/${unitCount}`}
            />
          ) : null}
        </ModalTariffLine>
      </ModalHeader>

      <ModalFields container spacing={MODAL_FIELD_SPACING} alignItems='flex-start'>
        <TariffFormFields inputs={inputs} control={control} errors={errors} />
      </ModalFields>

      {/*
        The buttons are passed as nodes instead of through `primaryButtonLabel` and
        `onAccept`: in the design system's ModalActions the handlers are swapped, with
        `onAccept` wired to the secondary button and `onCancel` to the primary one.
        Passing the nodes sidesteps that without depending on the bug staying there.
      */}
      <ModalActions
        divider
        secondaryButton={
          <Button variant='outlined' onClick={onClose} startSlot={<IconX size={20} aria-hidden />}>
            {i18n('checkout_additional_info_cancel')}
          </Button>
        }
        primaryButton={
          <Button
            variant='contained'
            disabled={!canSubmit}
            onClick={handleAccept}
            endSlot={isLast ? undefined : <IconArrowRight size={20} aria-hidden />}
          >
            {isLast ? i18n('checkout_additional_info_done') : i18n('checkout_additional_info_next')}
          </Button>
        }
      />
    </>
  );
};

type EditAllStepProps = {
  queue: QueuedTariff[];
  productName: string;
  storeErrors: Record<string, string[]>;
  values: FormInputValues;
  onSaveFormInput: (patch: FormInputValues) => void;
  onClose: () => void;
};

const EditAllStep = (props: EditAllStepProps) => {
  const { queue, productName, storeErrors, values, onSaveFormInput, onClose } = props;
  const { i18n } = useTranslate();

  const inputs = queue.flatMap((entry) => entry.inputs);
  const { control, errors, canSubmit, submit } = useTariffFormInputsForm({ inputs, values, errors: storeErrors });

  const handleSave = submit((collected) => {
    onSaveFormInput(collected);
    onClose();
  });

  return (
    <>
      <ModalHeader title={productName} onClose={onClose} divider paddingBottom />

      <ModalStackedContent>
        {queue.map((entry, index) => (
          <React.Fragment key={formInputGroupKey(entry.inputs)}>
            {index > 0 ? <Divider flexItem /> : null}

            <ModalTariffBlock>
              <ModalFields container data-flush='true' spacing={MODAL_FIELD_SPACING} alignItems='flex-start'>
                <TariffFormFields inputs={entry.inputs} control={control} errors={errors} />
              </ModalFields>
            </ModalTariffBlock>
          </React.Fragment>
        ))}
      </ModalStackedContent>

      <ModalActions
        divider
        secondaryButton={
          <Button variant='outlined' onClick={onClose} startSlot={<IconX size={20} aria-hidden />}>
            {i18n('checkout_additional_info_cancel')}
          </Button>
        }
        primaryButton={
          <Button variant='contained' disabled={!canSubmit} onClick={handleSave}>
            {i18n('checkout_additional_info_save')}
          </Button>
        }
      />
    </>
  );
};

export const AdditionalInfoModal = (props: AdditionalInfoModalProps) => {
  const { open, mode, queue, productNameFor, storeErrors, values, onSaveFormInput, onStepChange, onClose } = props;

  const [step, setStep] = useState(0);
  const [wasOpen, setWasOpen] = useState(open);

  /**
   * Reopening always restarts the queue; whoever built it decides the starting point.
   * The reset happens here, in the render, and not in an effect: an effect only runs
   * after the paint, so the reopened modal would first draw `queue[step]` from the
   * previous visit — another tariff, or nothing at all when the new queue is shorter
   * than the step it was left at — and only then snap back to the start.
   */
  if (open !== wasOpen) {
    setWasOpen(open);
    setStep(0);
  }

  const current = queue[step];

  // The current item's accordion opens along with it, which is what keeps the filling
  // going when it turns from one cart item into the next.
  useEffect(() => {
    if (open && current) {
      onStepChange?.(current.saleItemId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only the step change matters
  }, [open, current?.saleItemId]);

  if (!open || !current) {
    return null;
  }

  return (
    <Modal open={open} onClose={onClose} width='640px' sx={responsiveModalSx}>
      <div data-testid='additional-info-modal' data-mode={mode} data-step={String(step + 1)}>
        {mode === ADDITIONAL_INFO_MODAL_MODE.edit ? (
          <EditAllStep
            queue={queue}
            productName={productNameFor(current.saleItemId)}
            storeErrors={storeErrors}
            values={values}
            onSaveFormInput={onSaveFormInput}
            onClose={onClose}
          />
        ) : (
          <WizardStep
            key={formInputGroupKey(current.inputs)}
            inputs={current.inputs}
            productName={productNameFor(current.saleItemId)}
            unitIndex={current.unitIndex}
            unitCount={current.unitCount}
            isLast={step >= queue.length - 1}
            storeErrors={storeErrors}
            values={values}
            onSaveFormInput={onSaveFormInput}
            onClose={onClose}
            onAdvance={() => setStep((step) => step + 1)}
          />
        )}
      </div>
    </Modal>
  );
};

export default AdditionalInfoModal;
